import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { observer } from "mobx-react";
import { Tabs, Tab, Typography, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Paper, Button, StandardDialog, SelectField } from '@roc/ui';
import { Layout, useUserStore } from "@roc/feature-app-core";
import { AddCadenceStepModal } from "./addCadenceStepModal";
import { OverviewCadenceDetailsTab } from "./overviewCadenceDetailsTab";
import { PeopleCadenceDetailsTab } from "./peopleCadenceDetailsTab";
import { useStore } from '@roc/client-portal-shared/hooks';
import { Roles } from "@roc/feature-utils";
import { Edit } from '@material-ui/icons';
import IconButton from '@material-ui/core/IconButton';
import { PorspectsByOriginatorDashboardTab } from "./porspectsByOriginatorDashboardTab";
import { ProspectsByOriginatorGridStoreV2 } from "libs/client-portal-shared/src/app/modules/companyProfile/stores/cadenceStore/prospectsByOriginatorGridStoreV2";
import { PorspectsByOriginatorDashboardTabV2 } from "./porspectsByOriginatorDashboardTabV2";
import { PeopleCadenceDetailsTabV2 } from "./peopleCadenceDetailsTabV2";


const useStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(4),
    minHeight: "60vh",
    display: "flex",
    flexDirection: "column"
  },
  backButton: {
    display: "flex",
    justifyContent: "flex-start",
    width: "100%",
    marginBottom: '20px'
  }
}));


export const CadenceDetailsHeader = ({ cadence, onNameChange, shouldRenderButton }) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [cadenceName, setCadenceName] = useState(cadence.cadence);

  const handleEditClick = () => {
    setIsEditingName(true);
  };

  const handleBlur = () => {
    setIsEditingName(false);
    onNameChange(cadenceName);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '20px' }}>
      {isEditingName ? (
        <TextField
          value={cadenceName}
          onChange={(e) => setCadenceName(e.target.value)}
          onBlur={handleBlur}
          autoFocus
          size="small"
          variant="outlined"
        />
      ) : (
        <>
          <Typography variant="h5">
            {`Cadence > ${cadence.cadence}`}
          </Typography>
          {shouldRenderButton &&
            <IconButton onClick={handleEditClick} size="small">
              <Edit fontSize="small" />
            </IconButton>
          }

        </>
      )}
    </div>
  );
};

interface CadenceDetailsProps {
  isBackofficeView: boolean
}

export const CadenceDetails = observer((props: CadenceDetailsProps) => {
  const { cadenceId } = useParams<{ cadenceId: string }>();
  const [activeTab, setActiveTab] = useState("overview");
  const [openAddCadenceModal, setOpenAddCadenceModal] = useState(false);
  const [openAddPeopleModal, setOpenAddPeopleModal] = useState(false);
  const [isLoadingCadence, setIsLoadingCadence] = useState(true);
  const { userStore } = useUserStore();
  const { cadenceStore } = useStore();
  const isBackOfficeLead = userStore.userInformation?.roles?.includes(Roles.BACK_OFFICE_LEAD);
  const classes = useStyles();
  const [hasMounted, setHasMounted] = useState(false);

  const handleOpenAddCadenceModal = () => setOpenAddCadenceModal(true);
  const handleCloseAddCadenceModal = () => setOpenAddCadenceModal(false);
  const handleOpenAddPeopleModal = () => setOpenAddPeopleModal(true);
  const handleCloseAddPeopleModal = () => setOpenAddPeopleModal(false);

  useEffect(() => {
    cadenceStore.resetOwners();
    const fetchData = async () => {
      cadenceStore.setCallingFromMyCadences(props.isBackofficeView)
      setIsLoadingCadence(true);
      if(isBackOfficeLead && !cadenceStore.callingFromMyCadences){
        cadenceStore.getOwners(Number(cadenceId));
        await cadenceStore.fetchCadenceContentDataWithProspectsByCadenceId(Number(cadenceId));
      }else{
        await cadenceStore.fetchCadenceContentDataByCadenceId(Number(cadenceId));
      }
      setIsLoadingCadence(false);
    };
    fetchData();
  }, [cadenceId]);

  useEffect(() => {
    if(isBackOfficeLead && cadenceStore.callingFromMyCadences) return;
    if (!hasMounted) {
      setHasMounted(true);
      return;
    }
    const fetchData = async () => {
      cadenceStore.setCallingFromMyCadences(props.isBackofficeView)
      setIsLoadingCadence(true);
      if(cadenceStore.selectedOriginator > 0){
        await cadenceStore.fetchCadenceContentDataByCadenceIdAndUserId(Number(cadenceId));
      }else{
        await cadenceStore.fetchCadenceContentDataWithProspectsByCadenceId(Number(cadenceId));
      }
      setIsLoadingCadence(false);
    };
    fetchData();
  }, [cadenceStore.selectedOriginator]);

  const handleChangeName = (newName) => {
    cadenceStore.updateCadenceName(Number(cadenceId), newName);
  }

  if (isLoadingCadence || !cadenceStore.cadence?.cadence) {
    return (
      <Layout maxWidth={'lg'}>
        <Paper className={classes.paper}>
          <Typography variant="h6">Loading cadence details...</Typography>
        </Paper>
      </Layout>
    );
  }

  return (
    <Layout maxWidth={'lg'}>
      <Paper className={classes.paper} style={{ flexGrow: 1 }}>
        <div className={classes.backButton}>
          <Button
            testId="backButton"
            variant="outlined"
            onClick={() => window.history.back()}
            style={{ minWidth: "auto" }}
          >
            ← Back
          </Button>
        </div>

        <CadenceDetailsHeader
          cadence={cadenceStore.cadence}
          onNameChange={(newName) => handleChangeName(newName)}
          shouldRenderButton={isBackOfficeLead}
        />

        {(isBackOfficeLead && cadenceStore.owners && cadenceStore.owners.length > 1) && (
          <div style={{ marginBottom: 20, marginTop: '2em', width: '12em' }}>
            <SelectField
              label={'Select an Originator'}
              options={cadenceStore.owners}
              testId={'owners'}
              value={cadenceStore.selectedOriginator}
              variant={'outlined'}
              onChange={value => cadenceStore.setSelectedOriginator(value)}
              style={{ height: '35px', width: '10em'}}
            />
          </div>
        )}

        <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)}>
          <Tab label="Overview" value="overview" />
          <Tab label="Prospects" value="people" />
          {isBackOfficeLead &&
            <Tab label="Prospects By Originator" value="originatorProspects" />
          }
        </Tabs>

        {activeTab === "overview" && (
          <OverviewCadenceDetailsTab
            cadence={cadenceStore.cadence}
            handleOpenModal={handleOpenAddCadenceModal}
          />
        )}

        {activeTab === "people" && (
          //<PeopleCadenceDetailsTab shouldRenderButton={isBackOfficeLead} listViewId={cadenceStore.cadence.listViewId} gritStore={cadenceStore.cadenceUsersDashboardGridStore} />
          <PeopleCadenceDetailsTabV2 gritStore={cadenceStore.cadenceUsersDashboardGridStoreV2} />
        )}

        {activeTab === "originatorProspects" && (
          //<PorspectsByOriginatorDashboardTab listViewId={cadenceStore.cadence.listViewId} gritStore={cadenceStore.cadenceProspectsByOriginatorGridStore} />
          <PorspectsByOriginatorDashboardTabV2 gritStore={cadenceStore.cadenceProspectsByOriginatorGridStoreV2} />
        )}
      </Paper>

      <AddCadenceStepModal
        openModal={openAddCadenceModal}
        handleCloseModal={handleCloseAddCadenceModal}
      />
    </Layout>
  );
});
